import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal } from '@mui/material';
import { omit } from 'lodash';
import { DatasetCreation } from '~/api/types';
import { useCreateDatasetMutation, useEditDatasetMutation, useGetDatasetQuery } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalContainerStyle } from '~/components/styles';
import { useHasLockedStatus } from '~/hooks/dataset';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';
import { closeModalById } from '~/redux/slices/modalSlice';
import { dataTagsSchema, datasetDetailsSchema, datasetSchema } from '~/validation';
import { AddDataMembersStep, DataTagsStep, DatasetDetailsStep, DatasetStepper } from '.';
import { DatasetCreationStepsEnum, initialDefaults } from './types';

const DatasetModal = ({ id }: { id: string }) => {
  const { id: routeDatasetId, region } = useParams();
  const datasetId = +routeDatasetId!;
  const { data } = useGetDatasetQuery({ datasetId }, { skip: !datasetId });
  const user = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasLockedStatus } = useHasLockedStatus();
  const [createDataset, { isLoading }] = useCreateDatasetMutation({
    fixedCacheKey: `create-dataset`,
  });
  const [editDataset, { isLoading: isEditing }] = useEditDatasetMutation({ fixedCacheKey: `edit-dataset` });
  const [activeStep, setActiveStep] = useState<DatasetCreationStepsEnum>(DatasetCreationStepsEnum.DETAILS);

  const stepSchema =
    activeStep === DatasetCreationStepsEnum.DETAILS
      ? datasetDetailsSchema
      : activeStep === DatasetCreationStepsEnum.DATA_TAGS
        ? dataTagsSchema
        : datasetSchema;

  const formMethods = useForm<DatasetCreation>({
    mode: 'all',
    reValidateMode: 'onChange',
    context: { isEdit: !!datasetId, hasLockedStatus },
    resolver: yupResolver(stepSchema),
    defaultValues: {
      ...initialDefaults,
      data_owner_name: user?.id,
      data_owner_network_id: user?.profile.NTID,
      source_ou: user?.operating_unit_description,
    },
  });

  const {
    reset,
    formState: { isDirty },
  } = formMethods;

  useEffect(() => {
    if (!data) return;
    reset(
      {
        name: data.name,
        description: data.description,
        data_classification: data.data_classification,
        provenance: data.provenance,
        source_ou: data.source_ou,
        type: data.type,
        data_format: data.data_format,
        collection_source: data.collection_source,
        de_identified: data.de_identified,
        restriction_tag: data.restriction_tag?.length ? data.restriction_tag : null,
        legal_relationship_tag: data.legal_relationship_tag,
        legal_relationship_qualifier_tag: data.legal_relationship_qualifier_tag,
        data_owner_name: data.data_owner_name,
        data_owner_network_id: data.data_owner_network_id,
        status: data.status,
        secondary_data_owners: data?.secondary_data_owners || [],
        secondary_data_owner_network_ids: data?.secondary_data_owner_network_ids || [],
        data_uploaders: data?.data_uploaders || [],
        data_uploaders_network_ids: data?.data_uploaders_network_ids || [],
        geo_location_tags: data.geo_location_tags,
        modality: data?.modality || [],
        anatomy: data?.anatomy || [],
        dimensionality: data?.dimensionality || [],
        mdt_id: data?.mdt_id,
        wbs_code_id: data?.wbs_code_id,
        cost_center_id: data?.cost_center_id,
        // mocked
        visit_type: false,
        have_sites: false,
      },
      { keepDirtyValues: false, keepTouched: false },
    );
  }, [data, dispatch]);

  const handleClose = () => {
    if (!(isLoading || isEditing)) {
      dispatch(closeModalById(id));
      if (!datasetId) reset(initialDefaults);
      setActiveStep(DatasetCreationStepsEnum.DETAILS);
    }
  };

  const onSubmit = async (data: DatasetCreation) => {
    try {
      const res = await createDataset(omit(data, ['status', 'visit_type', 'have_sites'])).unwrap();
      handleClose();
      navigate(`${region ? `/${region}` : ''}/data-catalog/${res.data.id}`);
    } catch (err) {
      console.error('Failed to create dataset: ', err);
    }
  };

  const onEdit = async (data: DatasetCreation) => {
    try {
      if (isDirty) {
        await editDataset({
          datasetId,
          ...omit(data, [
            'visit_type',
            'have_sites',
            ...(data.wbs_code_id === null && data.cost_center_id === null ? ['wbs_code_id', 'cost_center_id'] : []),
          ]),
        }).unwrap();
      }
      handleClose();
    } catch (err) {
      console.error('Failed to edit dataset: ', err);
    }
  };

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader
          onClose={handleClose}
          title={
            datasetId
              ? ['Edit Dataset', 'Please update the following details to edit the dataset.']
              : ['Add new dataset', 'Please enter the following details to add a new dataset']
          }
        />
        <FormProvider {...formMethods}>
          <DatasetStepper activeStep={activeStep} setActiveStep={setActiveStep} />
          {activeStep === DatasetCreationStepsEnum.DETAILS && <DatasetDetailsStep setActiveStep={setActiveStep} />}
          {activeStep === DatasetCreationStepsEnum.DATA_TAGS && <DataTagsStep setActiveStep={setActiveStep} />}
          {activeStep === DatasetCreationStepsEnum.DATA_MEMBERS && (
            <AddDataMembersStep onEdit={onEdit} onSubmit={onSubmit} setActiveStep={setActiveStep} />
          )}
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default DatasetModal;
